import React, { useRef } from 'react';
import { Box, Flex, Text, Badge } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import moment from 'moment';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import { IPessoalEmpregado } from '../../types';
import { UI } from '../../ui';
import { CollabAvatar, statusOf, CollabStatus } from './CollabAvatar';
import { classifyCargo, genderKey, prettyCargo } from './jobArchetypes';

const STATUS_META: Record<CollabStatus, { label: string; bg: string; fg: string }> = {
  ativo: { label: 'Ativo', bg: '#DCFCE7', fg: '#15803D' },
  demitido: { label: 'Demitido', bg: '#EEF2F6', fg: '#64748B' },
  afastado: { label: 'Afastado', bg: '#FEF3C7', fg: '#B45309' },
  outro: { label: 'Outro', bg: '#EEF2FF', fg: '#4F46E5' },
};

/** Parse tolerante (ISO ou BR) -> anos com 1 casa (0 se inválida). */
export function anosDeCasa(admissao?: string | null): number {
  if (!admissao) return 0;
  const d = moment(admissao, ['YYYY-MM-DD', 'DD/MM/YYYY', moment.ISO_8601], true);
  if (!d.isValid()) return 0;
  return Math.max(0, Math.round(moment().diff(d, 'years', true) * 10) / 10);
}

interface CollabCardProps {
  emp: IPessoalEmpregado;
  /** Pessoa com afastamento aberto no período (cruzado na galeria). */
  afastado?: boolean;
  index?: number;
  onSelect?: (emp: IPessoalEmpregado) => void;
}

export const CollabCard = React.memo(function CollabCard({
  emp,
  afastado,
  index = 0,
  onSelect,
}: CollabCardProps) {
  const tiltRef = useRef<HTMLDivElement>(null);
  const arch = classifyCargo(emp.cargo);
  const gender = genderKey(emp.sexo);
  const st = statusOf(emp.status, afastado);
  const anos = anosDeCasa(emp.admissao);
  const sm = STATUS_META[st];

  // Tilt 3D sem re-render: escreve o transform direto no nó (perf p/ grades grandes).
  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = tiltRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(680px) rotateX(${(-py * 7).toFixed(2)}deg) rotateY(${(px * 9).toFixed(2)}deg) translateZ(0)`;
  };
  const reset = () => {
    if (tiltRef.current)
      tiltRef.current.style.transform = 'perspective(680px) rotateX(0) rotateY(0)';
  };

  const nivelPct = Math.min(anos / 10, 1) * 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, delay: Math.min(index * 0.015, 0.4) }}
      style={{ height: '100%' }}
    >
      <Box
        ref={tiltRef}
        onMouseMove={onMove}
        onMouseLeave={reset}
        onClick={() => onSelect?.(emp)}
        role={onSelect ? 'button' : undefined}
        cursor={onSelect ? 'pointer' : 'default'}
        h="full"
        position="relative"
        overflow="hidden"
        borderRadius="18px"
        p={4}
        bg="rgba(255,255,255,0.72)"
        backdropFilter="blur(14px) saturate(160%)"
        border="1px solid rgba(255,255,255,0.85)"
        boxShadow="0 10px 30px -14px rgba(15,23,42,0.35)"
        transition="box-shadow .25s ease, transform .12s ease"
        sx={{ transformStyle: 'preserve-3d', willChange: 'transform' }}
        _hover={{ boxShadow: `0 22px 46px -18px ${arch.accent}80` }}
      >
        {/* glow do arquétipo */}
        <Box
          position="absolute"
          top="-44px"
          insetEnd="-44px"
          w="150px"
          h="150px"
          borderRadius="full"
          bg={`radial-gradient(circle, ${arch.accent}30, transparent 68%)`}
          pointerEvents="none"
        />

        <Flex gap={3.5} align="center" position="relative">
          <CollabAvatar gender={gender} archetype={arch} status={st} anos={anos} size={72} />
          <Box minW={0} flex={1}>
            <Text fontWeight={800} fontSize="14.5px" color={UI.ink} noOfLines={1} lineHeight="1.2">
              {emp.nome}
            </Text>
            <Text fontSize="12.5px" color={UI.muted} noOfLines={1} mt={0.5}>
              {prettyCargo(emp.cargo)}
            </Text>
            <Flex align="center" gap={1.5} mt={1.5} wrap="wrap">
              <Badge
                style={{ background: `${arch.accent}1A`, color: arch.accent }}
                borderRadius="full"
                px={2}
                py={0.5}
                fontSize="10px"
                fontWeight={700}
                textTransform="none"
              >
                {arch.label}
              </Badge>
              <Badge
                style={{ background: sm.bg, color: sm.fg }}
                borderRadius="full"
                px={2}
                py={0.5}
                fontSize="10px"
                fontWeight={700}
                textTransform="none"
              >
                {sm.label}
              </Badge>
            </Flex>
          </Box>
        </Flex>

        {/* HUD inferior */}
        <Box mt={3.5} pt={3} borderTop="1px solid rgba(15,23,42,0.07)" position="relative">
          <Flex justify="space-between" align="baseline" mb={2}>
            <Box>
              <Text
                fontSize="9.5px"
                fontWeight={800}
                color={UI.faint}
                textTransform="uppercase"
                letterSpacing="0.08em"
              >
                Salário
              </Text>
              <Text fontSize="14px" fontWeight={800} color={UI.ink} lineHeight="1.1">
                {formatCurrency(emp.salario ?? 0)}
              </Text>
            </Box>
            <Box textAlign="right">
              <Text
                fontSize="9.5px"
                fontWeight={800}
                color={UI.faint}
                textTransform="uppercase"
                letterSpacing="0.08em"
              >
                {emp.departamento || 'Sem depto'}
              </Text>
              <Text fontSize="12px" fontWeight={600} color={UI.muted}>
                {emp.vinculo || '—'}
              </Text>
            </Box>
          </Flex>

          {/* barra de tempo de casa (estilo XP) */}
          <Flex align="center" gap={2}>
            <Box flex={1} h="6px" borderRadius="full" bg="rgba(15,23,42,0.08)" overflow="hidden">
              <Box
                h="100%"
                borderRadius="full"
                bgGradient={`linear(to-r, ${arch.gradient[0]}, ${arch.gradient[1]})`}
                w={`${Math.max(nivelPct, 4)}%`}
              />
            </Box>
            <Text fontSize="11px" fontWeight={700} color={UI.muted} flexShrink={0}>
              {anos > 0 ? `${anos} ${anos === 1 ? 'ano' : 'anos'}` : 'novo'}
            </Text>
          </Flex>
        </Box>
      </Box>
    </motion.div>
  );
});
